import type {ReactNode} from 'react';

import {cn} from '@ds/helpers';

export type PickerSize = 'sm' | 'md' | 'lg';
export type PickerStatus = 'error' | 'warning';

const sizeClasses: Record<PickerSize, string> = {
  sm: '[&_.ant-picker]:min-h-8 [&_.ant-picker-input]:min-h-8 [&_.ant-picker-input>input]:text-[12px] [&_.ant-picker-input>input]:leading-[18px]',
  md: '[&_.ant-picker]:min-h-10 [&_.ant-picker-input]:min-h-10 [&_.ant-picker-input>input]:text-[14px] [&_.ant-picker-input>input]:leading-[22px]',
  lg: '[&_.ant-picker]:min-h-12 [&_.ant-picker-input]:min-h-12 [&_.ant-picker-input>input]:text-[14px] [&_.ant-picker-input>input]:leading-[24px]'
};

function statusBorderClasses(status?: PickerStatus) {
  if (status === 'error') {
    return '[&_.ant-picker]:border-destructive [&_.ant-picker:hover]:border-destructive [&_.ant-picker-focused]:border-destructive [&_.ant-picker-focused]:shadow-[0_0_0_2px_var(--color-destructive-surface)]';
  }
  if (status === 'warning') {
    return '[&_.ant-picker]:border-yellow [&_.ant-picker:hover]:border-yellow [&_.ant-picker-focused]:border-yellow [&_.ant-picker-focused]:shadow-[0_0_0_2px_var(--color-yellow-soft)]';
  }
  return '[&_.ant-picker]:border-disabled-border [&_.ant-picker:hover]:border-orange [&_.ant-picker-focused]:border-orange [&_.ant-picker-focused]:shadow-[0_0_0_2px_var(--color-brand-surface)]';
}

export function pickerShellClasses(
  size: PickerSize = 'lg',
  status?: PickerStatus,
  className?: string
) {
  return cn(
    'w-full',
    '[&_.ant-picker]:w-full [&_.ant-picker]:rounded-lg [&_.ant-picker]:border [&_.ant-picker]:bg-white',
    '[&_.ant-picker-input>input]:text-right [&_.ant-picker-input>input]:text-gray-08',
    '[&_.ant-picker-suffix]:text-gray-06',
    sizeClasses[size],
    statusBorderClasses(status),
    className
  );
}

export function PickerFieldShell({
  id,
  label,
  required,
  help,
  wrapperClassName,
  children
}: {
  id?: string;
  label?: ReactNode;
  required?: boolean;
  help?: ReactNode;
  wrapperClassName?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('flex w-full flex-col gap-1.5', wrapperClassName)}>
      {label ? (
        <label htmlFor={id} className="text-[14px] font-medium leading-[21px] text-gray-08">
          {label}
          {required ? <span className="text-destructive">*</span> : null}
        </label>
      ) : null}
      {children}
      {help ? (
        <div className="flex items-center gap-1.5 px-2 text-[10px] leading-4 text-destructive">
          <i className="icon-Info-Outlined text-[16px]" aria-hidden />
          <span>{help}</span>
        </div>
      ) : null}
    </div>
  );
}
